
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import DesktopNavigation from './navbar/DesktopNavigation';
import DesktopAuthActions from './navbar/DesktopAuthActions';
import MobileMenu from './navbar/MobileMenu';
import RoleBadge from './navbar/RoleBadge';

const Navbar = () => {
  const { direction } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole, isSuper, isAdminOrHigher, loading: roleLoading } = useRolePermissions();

  // Check authentication status on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        setIsLoggedIn(!!data.session);
      } catch (error) {
        console.error('Error checking auth:', error);
      }
    };
    
    checkAuth();
    
    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      setIsLoggedIn(!!session);
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Navigate to login page
  const handleLogin = () => {
    navigate('/login', { state: { from: location.pathname } });
  };

  // Go to dashboard if logged in
  const handleDashboard = () => {
    navigate('/dashboard');
  };

  const renderRoleBadge = () => {
    return <RoleBadge userRole={userRole} loading={roleLoading} />;
  };

  return (
    <header 
      className={cn(
        'fixed w-full z-50 transition-all duration-300',
        isScrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'
      )}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <a href="#" className="font-bold text-xl text-synagogue-blue">
              {direction === 'rtl' ? 'לוח דיגיטלי' : 'Digital Board'}
            </a>
          </div>

          {/* Desktop Navigation */}
          <DesktopNavigation direction={direction} />

          {/* Desktop Auth Actions */}
          <DesktopAuthActions 
            isLoggedIn={isLoggedIn}
            userRole={userRole}
            isAdminOrHigher={isAdminOrHigher}
            isSuper={isSuper}
            handleLogin={handleLogin}
            handleDashboard={handleDashboard}
          />

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-3">
            <LanguageSelector size="sm" variant="ghost" />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-synagogue-blue p-2"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <MobileMenu 
            isLoggedIn={isLoggedIn}
            handleLogin={handleLogin}
            handleDashboard={handleDashboard}
            isSuper={isSuper}
            renderRoleBadge={renderRoleBadge}
            userRole={userRole}
            direction={direction}
          />
        )}
      </div>
    </header>
  );
};

export default Navbar;
